import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { repositoryCall, type RepositoryResult } from "./repositoryResult.js";
import { normalizeCalendarTasksResponse } from "../calendar/calendarResponseNormalization.js";
import { throwCmtsCanonicalError } from "../api/cmtsApiErrors.js";

export type CalendarRepository = {
  profiles(tenantId?: string, query?: Record<string, unknown>): Promise<RepositoryResult<readonly Record<string, unknown>[]>>;
  create(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  reschedule(eventId: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  cancel(eventId: string, tenantId?: string): Promise<RepositoryResult<unknown>>;
  holidays(tenantId?: string): Promise<RepositoryResult<readonly Record<string, unknown>[]>>;
  createHoliday(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  updateHoliday(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  deactivateHoliday(id: string, tenantId?: string): Promise<RepositoryResult<unknown>>;
  shifts(tenantId?: string): Promise<RepositoryResult<readonly Record<string, unknown>[]>>;
  createShift(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  updateShift(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  deactivateShift(id: string, tenantId?: string): Promise<RepositoryResult<unknown>>;
  checkWorkingTime(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  addDuration(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  calculateDueDate(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  runScheduledJob(jobId: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  retryScheduledJob(jobId: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
};

function readString(payload: Record<string, unknown>, keys: readonly string[], fallback = ""): string {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" || typeof value === "boolean") return String(value);
  }
  return fallback;
}

function slugCode(value: string, prefix: string): string {
  const cleaned = value.trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toUpperCase();
  return cleaned ? `${prefix}-${cleaned}`.slice(0, 80) : `${prefix}-${Date.now().toString(36).toUpperCase()}`;
}

function requireJsonText(value: string, fieldName: string): string {
  try {
    JSON.parse(value);
    return value;
  } catch {
    throwCmtsCanonicalError({
      code: "CMTS_ERR_INVALID_PAYLOAD",
      message: `${fieldName} must be valid JSON.`,
      details: { fieldName },
    });
  }
}

function toCalendarUpsertRequest(
  payload: Record<string, unknown>,
  tenantId?: string
): Record<string, unknown> {
  const name = readString(
    payload,
    ["name", "calendarName", "title"]
  );

  const code = readString(
    payload,
    ["code", "calendarCode"],
    slugCode(name || "CMTS Business Calendar", "CAL")
  );

  return {
    tenantId,
    calendarCode: code,
    calendarName: name,
    calendarType: readString(
      payload,
      ["calendarType", "eventType", "type"],
      "BUSINESS"
    ),
    timeZoneId: readString(
      payload,
      ["timeZone", "timeZoneId", "timezone"],
      "Asia/Kolkata"
    ),
    workingDaysJson: requireJsonText(readString(payload, ["workingDaysJson"], "[1,2,3,4,5]"), "workingDaysJson"),
    workingHoursJson: requireJsonText(readString(payload, ["workingHoursJson"], "{\"start\":\"09:00\",\"end\":\"18:00\"}"), "workingHoursJson"),
    effectiveFrom: readString(payload, ["effectiveFrom", "effectiveFromUtc"], new Date().toISOString().slice(0, 10)),
    effectiveTo: readString(payload, ["effectiveTo", "effectiveToUtc"], "") || undefined,
    expectedRowVersion: payload.rowVersion ?? payload.expectedRowVersion,
    isDefault:
      payload.isDefault === true ||
      String(payload.isDefault ?? "").toLowerCase() === "true",
    isActive:
      payload.isActive === undefined
        ? true
        : payload.isActive === true ||
          String(payload.isActive).toLowerCase() === "true",
  };
}

function toDueDateRequest(payload: Record<string, unknown>, tenantId?: string): Record<string, unknown> {
  const durationHoursText = readString(payload, ["durationHours"], "");
  const durationMinutesText = readString(payload, ["durationMinutes", "duration"], "");
  const duration = durationMinutesText ? Number(durationMinutesText) : durationHoursText ? Number(durationHoursText) * 60 : 60;
  return {
    tenantId,
    calendarCode: readString(payload, ["calendarCode"], "CAL-INDIA-STANDARD"),
    startAtUtc: readString(payload, ["startAtUtc", "startAt"], new Date().toISOString()),
    duration: Number.isFinite(duration) && duration > 0 ? duration : 60,
    durationUnit: readString(payload, ["durationUnit"], "Minutes"),
    correlationId: readString(payload, ["correlationId"], "") || undefined,
  };
}

function toHolidayRequest(payload: Record<string, unknown>, tenantId?: string): Record<string, unknown> {
  return {
    tenantId,
    calendarCode: readString(payload, ["calendarCode", "code"]),
    holidayDate: readString(payload, ["holidayDate", "date"]),
    holidayName: readString(payload, ["holidayName", "name", "title"]),
    holidayType: readString(payload, ["holidayType", "type"], "PUBLIC"),
    regionCode: readString(payload, ["regionCode", "region"], "") || undefined,
    siteId: readString(payload, ["siteId"], "") || undefined,
    departmentId: readString(payload, ["departmentId"], "") || undefined,
  };
}

function toShiftRequest(payload: Record<string, unknown>, tenantId?: string): Record<string, unknown> {
  return {
    tenantId,
    calendarCode: readString(payload, ["calendarCode", "code"]),
    shiftCode: readString(payload, ["shiftCode"], slugCode(readString(payload, ["shiftName", "name"], "SHIFT"), "SHIFT")),
    shiftName: readString(payload, ["shiftName", "name", "title"]),
    startTime: readString(payload, ["startTime"], "09:00"),
    endTime: readString(payload, ["endTime"], "18:00"),
    breakMinutes: Number(readString(payload, ["breakMinutes"], "60")),
    workingDayCodes: readString(payload, ["workingDayCodes"], "MON,TUE,WED,THU,FRI"),
    siteId: readString(payload, ["siteId"], "") || undefined,
    departmentId: readString(payload, ["departmentId"], "") || undefined,
  };
}

export function createCalendarRepository(client: CmtsHttpClient): CalendarRepository {
  return {
    profiles: (tenantId, query = {}) => repositoryCall(async () => normalizeCalendarTasksResponse(await client.get<unknown>(CMTS_API.adminCalendars.list, { query: { tenantId, ...(query as Record<string, string | number | boolean | undefined>) } })), "CMTS_CALENDAR_PROFILES"),
    create: (payload, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminCalendars.create, toCalendarUpsertRequest(payload, tenantId), { query: { tenantId } }), "CMTS_CALENDAR_PROFILE_CREATE"),
    reschedule: (eventId, payload, tenantId) => repositoryCall(() => client.put<unknown>(CMTS_API.adminCalendars.update(eventId), toCalendarUpsertRequest(payload, tenantId), { query: { tenantId } }), "CMTS_CALENDAR_EVENT_RESCHEDULE"),
    cancel: (eventId, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminCalendars.deactivate(eventId), undefined, { query: { tenantId } }), "CMTS_CALENDAR_EVENT_CANCEL"),
    holidays: (tenantId) => repositoryCall(async () => normalizeCalendarTasksResponse(await client.get<unknown>(CMTS_API.adminCalendars.holidayList, { query: { tenantId } })), "CMTS_CALENDAR_HOLIDAYS"),
    createHoliday: (payload, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminCalendars.createHoliday, toHolidayRequest(payload, tenantId)), "CMTS_CALENDAR_HOLIDAY_CREATE"),
    updateHoliday: (id, payload, tenantId) => repositoryCall(() => client.put<unknown>(CMTS_API.adminCalendars.updateHoliday(id), toHolidayRequest(payload, tenantId)), "CMTS_CALENDAR_HOLIDAY_UPDATE"),
    deactivateHoliday: (id, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminCalendars.deactivateHoliday(id), undefined, { query: { tenantId } }), "CMTS_CALENDAR_HOLIDAY_DEACTIVATE"),
    shifts: (tenantId) => repositoryCall(async () => normalizeCalendarTasksResponse(await client.get<unknown>(CMTS_API.adminCalendars.shiftList, { query: { tenantId } })), "CMTS_CALENDAR_SHIFTS"),
    createShift: (payload, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminCalendars.createShift, toShiftRequest(payload, tenantId)), "CMTS_CALENDAR_SHIFT_CREATE"),
    updateShift: (id, payload, tenantId) => repositoryCall(() => client.put<unknown>(CMTS_API.adminCalendars.updateShift(id), toShiftRequest(payload, tenantId)), "CMTS_CALENDAR_SHIFT_UPDATE"),
    deactivateShift: (id, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminCalendars.deactivateShift(id), undefined, { query: { tenantId } }), "CMTS_CALENDAR_SHIFT_DEACTIVATE"),
    checkWorkingTime: (payload, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminCalendars.checkWorkingTime, { tenantId, calendarCode: readString(payload, ["calendarCode"]), evaluateAtUtc: readString(payload, ["evaluateAtUtc", "at"], new Date().toISOString()) }), "CMTS_CALENDAR_WORKING_TIME_CHECK"),
    addDuration: (payload, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminCalendars.addBusinessDuration, toDueDateRequest(payload, tenantId)), "CMTS_CALENDAR_DURATION_ADD"),
    calculateDueDate: (payload, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminCalendars.calculateDueDate, toDueDateRequest(payload, tenantId), { query: { tenantId } }), "CMTS_CALENDAR_DUE_DATE_TEST"),
    runScheduledJob: (jobId, payload, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.adminScheduledJobs.runNow(jobId), payload, { query: { tenantId } }), "CMTS_SCHEDULED_JOB_RUN_NOW"),
    retryScheduledJob: (jobId, payload, tenantId) => repositoryCall(() => client.post<unknown>(`/admin/scheduled-jobs/${encodeURIComponent(jobId)}/retry`, payload, { query: { tenantId } }), "CMTS_SCHEDULED_JOB_RETRY"),
  };
}
